import React, {useEffect, useState} from 'react';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {ICombinedData, IDataSetConfig} from "../common/api/interfaces/interfaces";
import {useGetDataInfoRange} from "../../../../api/api-hooks/apiHooks";
import {formatDateByDateFrequency, getExtractedDataValue, remakeArrayIntoOtherInterface} from "../utils";
import {Button} from "primereact/button";

import {ChronoUnit} from "../../../../utils/time-utils";
import InputNumber from "../../../basic/input-number/InputNumber";
import {InputSwitch} from "primereact/inputswitch";
import {DatasetDataInfo} from "../../../../api/DatasetDataApi";
import {UpdateDatasetData} from "../../../../api/requests";
import {ToastSeverityType} from "primereact/toast";
import CalendarWithDebounce from "../../../basic/calendar-with-debounce/CalendarWithDebounce";

import './DataInputOutputUpdateTable.scss';

type DataInputOutputUpdateTable = {
    identifiers: Array<string>
    firstLoadDate: Date
    datasets: Array<IDataSetConfig>
    dateFrequency: ChronoUnit

    showToast(severityValue: ToastSeverityType, summaryValue: string, detailValue: string): void
    titleColumnReader(rowData: ICombinedData): JSX.Element | undefined
    closeDialog(): void
}

const DataInputOutputUpdateTable = ({
                                        identifiers,
                                        titleColumnReader,
                                        firstLoadDate,
                                        datasets,
                                        dateFrequency,
                                        closeDialog,
                                        showToast
                                    }: DataInputOutputUpdateTable) => {
    const [dataQuery, setDataQuery] = useState<Array<any>>([])

    const [date, setDate] = useState<Date>(formatDateByDateFrequency(dateFrequency, firstLoadDate || new Date()));

    const fetchQuery = useGetDataInfoRange(identifiers,
        {timeStart: date.toISOString() || firstLoadDate.toISOString()},
        {
            enabled: date && identifiers.length > 0
        })

    useEffect(() => {
        if (fetchQuery.data && !fetchQuery.isError && !fetchQuery.isLoading && fetchQuery.data?.length > 0) {
            setDataQuery([...remakeArrayIntoOtherInterface(fetchQuery.data, datasets)])
        }
    }, [fetchQuery.data])

    useEffect(() => {
        fetchQuery.refetch();
    }, [date])

    const updateData = () => {
        let tempArr: Array<UpdateDatasetData | undefined> = dataQuery.map(rowData => {
            let tempOb = fetchQuery.data?.find((ob:any) => ob.identifier === rowData.identifier)
            if (tempOb && rowData.data.length > 0 &&
                tempOb.data !== rowData.data) {
                if (tempOb.data.length === 0 && !rowData.data[0].value) {
                    return
                }
                if (tempOb.data.length > 0 && tempOb.data[0].value === rowData.data[0].value) {
                    return
                }

                const objVal: { [k: string]: any } = {};

                let dateString = date.toISOString();
                if (rowData.data.length > 0) {
                    dateString = rowData.data[0].key
                }

                objVal[`${dateString}`] = rowData.data[0].value;

                return {
                    "identifier": rowData.identifier,
                    "values": objVal
                }
            }
        }).filter(ob => ob != null)

        if (tempArr.length === 0) {
            showToast("warn", "", "No changes made")
            return
        }
        DatasetDataInfo.updateData(tempArr as Array<UpdateDatasetData>)
            .then(() => {
                showToast("success", "", "Datasets updated")
                closeDialog()
            })
            .catch(er => {
                console.error(er)
                showToast("error", "", "Error while updating datasets")
                closeDialog()
            })
    }

    const valueBodyInputTemplate = (rowData: ICombinedData) => {
        if (rowData.type === "NUMBER") {
            let suffix = "";

            if (rowData.unit.length > 0 && rowData.unit !== " ") {
                suffix = " " + rowData.unit;
            }

            return <InputNumber value={getExtractedDataValue(rowData.data, rowData.decimalPlaces)}
                                onChange={(e) => {
                                    valueChangeHandler(rowData.identifier as string, e)
                                }}
                                decimalPlace={rowData.decimalPlaces}
                                suffix={suffix}
                                placeholder={suffix}
            />
        }
        if (rowData.type === "BOOLEAN") {
            return <InputSwitch
                checked={getExtractedDataValue(rowData.data, 0) == 1}
                onChange={(e) => {
                    valueChangeHandler(rowData.identifier as string, e.value ? 1 : 0)
                }}
            />
        }

        return <>[unknown type]</>
    }

    const valueChangeHandler = (identifier: string, newValue: any) => {
        const tempArr = dataQuery.map(item => {
            if (item.identifier === identifier) {
                return {
                    ...item,
                    data: item.data.length > 0
                        ? [{...item.data[0], value: newValue}]
                        : [{key: `${date.toISOString()}`, value: newValue}],
                };
            }
            return item;
        });
        setDataQuery(tempArr);
    };


    const onCancelClickHandler = () => {
        closeDialog()
    }

    return <>
        {fetchQuery.isError &&
            <>Error...</>
        }

        {!fetchQuery.isError &&
            <div className={"full-dialog-main-window"}>
                <div>
                    <CalendarWithDebounce value={date}
                                          onChange={setDate}
                                          dateFrequency={dateFrequency}
                    />
                </div>

                <DataTable value={dataQuery}
                           editMode="cell"
                           className="editable-cells-table"
                           responsiveLayout="scroll"
                           scrollHeight={"50vh"}
                           loading={fetchQuery.isLoading}
                           size="small">
                    <Column field={"title"}
                            header="Dataset"
                            body={titleColumnReader}
                            style={{width: '90%'}}
                    />
                    <Column field={"unit"}
                            header="Value"
                            body={valueBodyInputTemplate}
                    />
                </DataTable>
                <div className={"button-container"}>
                    <Button label="Update" icon="pi pi-pencil" onClick={() => updateData()}
                            className="p-button-success p-button-rounded p-button-sm"/>
                    <Button label="Cancel" icon="pi pi-times" onClick={() => onCancelClickHandler()}
                            className="p-button-danger p-button-rounded p-button-sm"/>
                </div>
            </div>

        }
    </>
}

export default DataInputOutputUpdateTable;